import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const Prijzen = () => {
  const { t } = useT();
  useSeo({ title: t("seo.prijzen.title"), description: t("seo.prijzen.desc"), image: "/og-image.png", path: "/prijzen" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("monthly");

  const tiers = [
    {
      id: "free",
      name: t("prijzen.free.name"),
      price: "€0",
      features: [t("prijzen.free.f1"), t("prijzen.free.f2"), t("prijzen.free.f3"), t("prijzen.free.f4")],
    },
    {
      id: "premium",
      name: t("prijzen.premium.name"),
      price: "€9,95",
      highlight: true,
      features: [
        t("prijzen.premium.f1"),
        t("prijzen.premium.f2"),
        t("prijzen.premium.f3"),
        t("prijzen.premium.f4"),
        t("prijzen.premium.f5"),
      ],
    },
  ];

  const choosePremium = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const res = await api("/billing/create-checkout-session", {
        method: "POST",
        body: JSON.stringify({ tier: "premium", plan, origin_url: window.location.origin }),
      });
      if (res.url) {
        window.location.href = res.url;
      } else {
        toast.error(t("prijzen.checkoutError"));
      }
    } catch (e) {
      // Checkout should always go to Stripe now. Show a clear error instead of
      // silently routing to the waitlist.
      toast.error(e.message || t("prijzen.checkoutError"));
    } finally {
      setBusy(false);
    }
  };

  const isPremiumUser = user?.is_premium || (user?.subscription_tier && user.subscription_tier !== "free");

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <div className="text-center">
        <h1 className="font-display text-4xl font-extrabold text-navy">{t("prijzen.title")}</h1>
        <p className="mt-3 text-slate-500">{t("prijzen.subtitle")}</p>
      </div>

      <div className="mt-8 flex justify-center">
        <div className="inline-flex items-center rounded-full border border-border bg-white p-1 shadow-sm" data-testid="plan-toggle">
          <button
            data-testid="plan-monthly"
            onClick={() => setPlan("monthly")}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${plan === "monthly" ? "bg-brandblue text-white" : "text-slate-600"}`}
          >
            Maandelijks
          </button>
          <button
            data-testid="plan-annual"
            onClick={() => setPlan("annual")}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${plan === "annual" ? "bg-brandblue text-white" : "text-slate-600"}`}
          >
            Jaarlijks
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${plan === "annual" ? "bg-white/20 text-white" : "bg-success/15 text-success"}`}>2 mnd gratis</span>
          </button>
        </div>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            data-testid={`tier-${tier.id}`}
            className={`relative rounded-[2rem] border p-8 ${
              tier.highlight ? "border-brandblue bg-brandblue-light/40 shadow-xl" : "border-border bg-white shadow-sm"
            }`}
          >
            {tier.highlight && (
              <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-brandblue px-3 py-1 text-xs font-bold text-white">
                <Sparkles size={13} /> {t("prijzen.popular")}
              </span>
            )}
            <h2 className="font-display text-xl font-bold text-navy">{tier.name}</h2>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-display text-4xl font-extrabold text-navy" data-testid={`price-${tier.id}`}>
                {tier.id === "premium" ? (plan === "annual" ? "€79" : "€9,95") : tier.price}
              </span>
              {tier.id !== "free" && <span className="mb-1 text-sm text-slate-400">{tier.id === "premium" && plan === "annual" ? "/ jaar" : t("prijzen.month")}</span>}
            </div>
            {tier.id === "premium" && plan === "annual" && (
              <p className="mt-1 text-xs font-semibold text-success">Bespaar €40,40 per jaar t.o.v. maandelijks (€119,40)</p>
            )}
            <ul className="mt-6 space-y-3">
              {tier.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check size={18} className="shrink-0 text-success" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              {tier.id === "free" ? (
                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={() => navigate(user ? "/dashboard" : "/register")}
                  data-testid="tier-free-cta"
                >
                  {user ? t("prijzen.current") : t("nav.register")}
                </Button>
              ) : isPremiumUser ? (
                <Button disabled className="w-full rounded-xl" data-testid="tier-premium-cta">
                  {t("prijzen.current")}
                </Button>
              ) : (
                <Button
                  onClick={choosePremium}
                  disabled={busy}
                  className="w-full rounded-xl bg-brandblue hover:bg-brandblue/90"
                  data-testid="tier-premium-cta"
                >
                  {busy ? t("common.loading") : t("prijzen.choose")}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-8 text-center text-sm text-slate-500" data-testid="prijzen-billing-note">
        {t("prijzen.billingNote")}
      </p>
    </div>
  );
};

export default Prijzen;
